import React = require("react");
import { fieldValidation, PublicAmorgUser, userHelper, WorkgroupListing, WorkgroupType } from '../../../amorg-common';
import { addWorkgroupMember, removeWorkgroup, removeWorkgroupMember, updateWorkgroup } from "../../../api/workgroupClientApi";
import { Label } from "../../../designSystem/Label";
import { MessageCard } from "../../../designSystem/MessageCard";
import { Button } from "../../../designSystem/button/Button";
import { ButtonRow } from "../../../designSystem/button/ButtonRow";
import { colors } from "../../../designSystem/colors/color";
import { CloseModalButton } from "../../../designSystem/compositeComponents/CloseModalButton";
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { RadioButtonListField } from "../../../designSystem/formField/RadioButtonListField";
import { SelectField } from "../../../designSystem/formField/SelectField";
import { TextSaveField } from "../../../designSystem/formField/text/TextSaveField";
import { SelectOption } from "../../../designSystem/inputs/Select";
import { P } from "../../../designSystem/typography/Body";
import { txt } from "../../../txt";

type ParentSelection = {
    workgroupId: string;
    workgroupName: string;
    level: number;
};

type WorkgroupEditorProps = {
    allWorkgroups: WorkgroupListing[];
    allUsers: PublicAmorgUser[];
    inEditWorkgroup: WorkgroupListing;
    onUpdated: () => void;
    onDeleted: () => void;
    onClose: () => void;
}
export function WorkgroupEditor(props: WorkgroupEditorProps): JSX.Element {
    const [parentOptions, setParentOptions] = React.useState<ParentSelection[]>([]);

    // Based on change in allWorkgroups
    React.useEffect(() => {
        const options: ParentSelection[] = [];
        options.push({ workgroupId: '', workgroupName: 'ingen', level: 0 });

        function getChildOptions(parent: WorkgroupListing | null, parentLevel: number) {
            const children = props.allWorkgroups.filter(wg => wg.parentWorkgroupId === (parent?.id || ''));
            for (const child of children) {
                if (child.id !== props.inEditWorkgroup.id) {
                    options.push({ workgroupId: child.id, workgroupName: child.name, level: parentLevel + 1 });
                    getChildOptions(child, parentLevel + 1);
                }
            }
        }
        getChildOptions(null, 0);
        setParentOptions(options);
    }, [props.allWorkgroups, props.inEditWorkgroup]);

    async function handleParentChange(newValue: string) {
        await updateWorkgroup(props.inEditWorkgroup.id, newValue, props.inEditWorkgroup.name, props.inEditWorkgroup.type);
        props.onUpdated();
    }

    async function handleNameChange(newValue: string) {
        await updateWorkgroup(props.inEditWorkgroup.id, props.inEditWorkgroup.parentWorkgroupId, newValue, props.inEditWorkgroup.type);
        props.onUpdated();
    }

    async function handleTypeChange(value: string) {
        await updateWorkgroup(props.inEditWorkgroup.id, props.inEditWorkgroup.parentWorkgroupId, props.inEditWorkgroup.name, value as WorkgroupType);
        props.onUpdated();
    }

    async function handleSelectNewMember(value:string)
    {
        if(value){
            await addWorkgroupMember(props.inEditWorkgroup.id, value);
            props.onUpdated();
        }
    }

    async function handleRemoveUser(userId:string)
    {
        await removeWorkgroupMember(props.inEditWorkgroup.id, userId);
        props.onUpdated();
    }

    async function handleClickDelete() {
        await removeWorkgroup(props.inEditWorkgroup.id);
        props.onDeleted();
    }

    function getUserText(u:PublicAmorgUser)
    {
        return `${userHelper.getUserFullName(u.firstname,u.middlename,u.lastname)} ${u.currentRole ? '('+u.currentRole.title+')' : ''}`;
    }

    const selectableUsers = props.allUsers.filter(u => props.inEditWorkgroup.members.find(m => m.userId === u.id)===undefined)
        .sort((lhs,rhs)=>userHelper.getPublicUserFullName(lhs).localeCompare(userHelper.getPublicUserFullName(rhs)));

    let canDelete = props.allWorkgroups.find(wg => wg.parentWorkgroupId === props.inEditWorkgroup.id) === undefined;
    canDelete = canDelete && props.inEditWorkgroup.members.length === 0;

    let options:SelectOption[] = [];
    if(parentOptions !== null){
            options = parentOptions.map(o => {
            return new Option(o.workgroupName.padStart(o.workgroupName.length + o.level, ' - '), o.workgroupId)
        });
        for(const option of options){
            const wg = parentOptions.find(o => o.workgroupId === option.value);                 
            if(wg){
                let __html = wg.workgroupName;
                for(let i=0; i<wg.level; i++)
                    __html = '&nbsp;&nbsp;&nbsp;' + __html;
                option.value = option.value;
                option.__innerHtml = __html;
            }
        }
    }

    return (
        <div>
            {parentOptions !== null &&
                <Flex flexDirection="column" gap={16}>
                    
                    <TextSaveField
                        label="Gruppenavn"
                        value={props.inEditWorkgroup.name}
                        onSave={handleNameChange}
                        validator={(currentValue) => fieldValidation.workgroup.name(currentValue)
                            ? {valid:true}
                            : {valid:false, errorMessage:txt.fieldValidation.mandatoryText(100, currentValue)
                        }}
                    />

                    <SelectField
                        id="parent-id"
                        label="Undergruppe til"
                        selectedValue={props.inEditWorkgroup.parentWorkgroupId}
                        options={options}
                        onChange={handleParentChange}
                    />
                    
                    <RadioButtonListField
                        label="Vælg type af gruppe"
                        groupName="workgroup-type"
                        options={
                            [
                                {text:'Udvalg', value:'committee'},
                                {text:'Arbejdsgruppe', value:'workgroup'}
                            ]
                        }
                        selectedValue={props.inEditWorkgroup.type}
                        onChange={handleTypeChange} 
                    />

                    <div>
                        {props.inEditWorkgroup.members.length === 0 &&
                            <P>Ingen medlemmer tilføjet</P>
                        }

                        {props.inEditWorkgroup.members.length > 0 &&
                            <div>
                                <Label formIndent>Medlemmer af gruppen</Label>
                                <Flex flexDirection="column" gap={5}>
                                    {props.inEditWorkgroup.members.map(m =>
                                        <Flex flexDirection="row" key={m.userId} justifyContent="stretch" alignItems="center" gap={10}>

                                            <div style={{
                                                width:10,
                                                height:10,
                                                borderRadius:'50%',
                                                backgroundColor:colors.getUserRoleColor(m.roleId),
                                                flexGrow:0
                                            }} />

                                            <Flex flexDirection="column" style={{flexGrow:'1'}}>
                                                <Flex flexDirection="row" alignItems="center" justifyContent="stretch" gap={10}> 
                                                    <div style={{flexGrow:1}}>
                                                        {userHelper.getUserFullName(m.firstname,m.middlename,m.lastname)} 
                                                    </div>
                                                </Flex>
                                                <div style={{color:colors.text.light, fontSize:12}}>
                                                    {m.roleTitle || ' '}
                                                </div>
                                            </Flex>

                                            <div style={{flexGrow:0}}>
                                                <Button variant="text"
                                                    icon="actions_close"
                                                    text=""
                                                    onClick={() => handleRemoveUser(m.userId)} />
                                            </div>
                                        </Flex>
                                    )}
                                </Flex>
                            </div>
                        }
                    </div>
          
                    <SelectField 
                        label="" 
                        selectedValue={''}
                        options={
                            [
                                ...selectableUsers.map(u => { 
                                    return {
                                        text:getUserText(u),
                                        value:u.id 
                                    }})
                                ,{ text:'Tilføj medlem til gruppen...', value:''}
                            ]
                        } 
                        onChange={handleSelectNewMember}
                    />
                    
                    {props.inEditWorkgroup.members.length > 0 &&
                        <MessageCard variant="warning">
                            Gruppen kan ikke slettes da den indeholder medlemmer
                        </MessageCard>
                    }
                    <ButtonRow variant="space-between">
                        <Button variant="destructive-text"
                            onClick={handleClickDelete}
                            icon={"interface_actions_delete"} 
                            text={"Slet"}
                            disabled={!canDelete} />
                        
                        <CloseModalButton onClose={props.onClose} />
                    </ButtonRow>
                </Flex>
            }
        </div>
    );
}